import React, { useContext, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Tracker from '@openreplay/tracker';
import { AuthContext } from './context/AuthContext';
import pdfDataCache from './components/documents/pdfDataCache';
import ErrorBoundary from './components/ErrorBoundary';

// Eager load kritieke componenten (login flow)
import Login from './pages/Login.jsx';
import RequireAuth from './components/auth/RequireAuth';
import RequirePermission from './components/auth/RequirePermission';
import { PERMISSIONS } from './config/permissions';
import Navbar from './components/NavBar.jsx';

// Lazy load alle overige pagina's voor betere initial load performance
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Projects = lazy(() => import('./pages/Projects.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const AdminPanel = lazy(() => import('./pages/AdminPanel.jsx'));
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ProjectManagement = lazy(() => import('./pages/ProjectManagement'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const CreateProject = lazy(() => import('./pages/CreateProject'));
const Library = lazy(() => import('./pages/Library.jsx'));
const StofSuggesties = lazy(() => import('./pages/StofSuggesties.jsx'));
const Metrics = lazy(() => import('./pages/Metrics.jsx'));
const Stoffenpakketten = lazy(() => import('./pages/Stoffenpakketten.jsx'));

// Suspense fallback component
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      gap: 2,
    }}
  >
    <CircularProgress size={40} />
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      Laden...
    </Typography>
  </Box>
);

const NotFound = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: 2,
      textAlign: 'center',
      px: 2,
    }}
  >
    <Typography variant="h5">Pagina niet gevonden</Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Deze pagina bestaat niet (meer). Controleer de link of ga terug naar het dashboard.
    </Typography>
    <Button variant="contained" href="/">
      Naar dashboard
    </Button>
  </Box>
);

// OpenReplay tracker met privacy-first configuratie
// Wrapped in try-catch: a tracker crash must never take down the whole app
let tracker = null;
try {
  tracker = new Tracker({
    projectKey: import.meta.env.VITE_OPENREPLAY_KEY,
    capturePerformance: true,
    obscureTextEmails: true,
    obscureTextNumbers: true,
    obscureInputEmails: true,
    obscureInputNumbers: true,
    obscureInputDates: true,
    defaultInputMode: 1,
    network: {
      capturePayload: false,
      ignoreHeaders: ['authorization', 'cookie', 'x-api-key'],
      sanitizer: (data) => {
        if (data.url && data.url.includes('supabase')) {
          data.url = data.url.replace(/apikey=[^&]+/, 'apikey=[REDACTED]');
        }
        return data;
      },
    },
    consoleMethods: ['error', 'warn'],
  });

  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_TRACKING === 'true') {
    tracker.start();
  }
} catch (e) {
  console.warn('[OpenReplay] Tracker kon niet worden gestart:', e);
}

function App() {
  const location = useLocation();
  // 2FA-status komt centraal uit AuthContext (zelfde bron als RequireAuth en Login)
  const { user, loading, role, isFullyAuthenticated } = useContext(AuthContext);

  // Memory pressure monitoring en cache cleanup
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      pdfDataCache.cleanup();
    }, 60000);

    // Memory pressure detection (Chrome/Edge)
    if ('memory' in performance && 'onmemorywarning' in performance) {
      const handleMemoryWarning = () => {
        console.warn('Memory warning detected, clearing cache');
        pdfDataCache.handleMemoryPressure(75);
      };

      performance.addEventListener('memorywarning', handleMemoryWarning);

      return () => {
        clearInterval(cleanupInterval);
        performance.removeEventListener('memorywarning', handleMemoryWarning);
      };
    }

    return () => clearInterval(cleanupInterval);
  }, []);

  // Identify user in OpenReplay when logged in
  useEffect(() => {
    if (user && tracker) {
      // Gebruik gehashte identifier in plaats van email voor privacy
      tracker.setUserID(user.id);
      tracker.setMetadata('role', role || 'user');
    }
  }, [user, role]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const showNavbar = isFullyAuthenticated && location.pathname !== '/reset-password';

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {showNavbar && <Navbar />}
        <Box component="main" sx={{ flexGrow: 1, pt: showNavbar ? 8 : 0 }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/forgot-password"
                element={!user ? <ForgotPassword /> : <Navigate to="/login" replace />}
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/projects"
                element={
                  <RequireAuth>
                    <Projects />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.MANAGE_USERS}>
                      <AdminPanel />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/employee-management"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.MANAGE_EMPLOYEES}>
                      <EmployeeManagement />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/project-management"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.VIEW_PROJECT_MANAGEMENT}>
                      <ProjectManagement />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/projects/new"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.CREATE_PROJECT}>
                      <CreateProject />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <RequireAuth>
                    <Projects />
                  </RequireAuth>
                }
              />
              <Route
                path="/projects/:id/settings"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.EDIT_PROJECT}>
                      <ProjectSettings />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <Settings />
                  </RequireAuth>
                }
              />
              <Route
                path="/library"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.VIEW_LIBRARY}>
                      <Library />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/stof-suggesties"
                element={
                  <RequireAuth>
                    <StofSuggesties />
                  </RequireAuth>
                }
              />
              <Route
                path="/metrics"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.VIEW_METRICS}>
                      <Metrics />
                    </RequirePermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/stoffenpakketten"
                element={
                  <RequireAuth>
                    <RequirePermission permission={PERMISSIONS.MANAGE_STOFFENPAKKETTEN}>
                      <Stoffenpakketten />
                    </RequirePermission>
                  </RequireAuth>
                }
              />

              {/* Fallback: ingelogd -> 404-pagina, niet ingelogd -> login */}
              <Route
                path="*"
                element={
                  user ? (
                    <RequireAuth>
                      <NotFound />
                    </RequireAuth>
                  ) : (
                    <Navigate to="/login" state={{ from: location }} replace />
                  )
                }
              />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </ErrorBoundary>
  );
}

export default App;
